<template>
  <div v-if="visible" class="fixed bottom-4 left-4 right-4 md:left-8 md:right-auto md:bottom-8 z-50">
    <div
      class="bg-gradient-to-r from-green-600 to-green-400 text-white rounded-xl shadow-2xl p-4 max-w-3xl mx-auto flex items-start gap-4">
      <div class="flex-shrink-0">
        <svg class="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 2v4m0 12v4m8-8h-4M4 12H0" />
        </svg>
      </div>

      <div class="flex-1">
        <h3 class="text-lg font-semibold gradient-heading">Privacy & Cookies</h3>
        <p class="text-sm opacity-90 mt-1">We use cookies to improve the site and to provide analytics. Choose how you
          want
          analytics handled for this browser. You can change this later from your profile.</p>

        <div class="mt-3 flex items-center gap-3">
          <button @click="accept" class="px-4 py-2 bg-white text-green-700 rounded-lg font-medium">Accept</button>
          <button @click="decline"
            class="px-4 py-2 bg-white/10 border border-white/25 text-white rounded-lg">Decline</button>
          <button @click="manage" class="px-3 py-1 bg-white/10 text-white rounded">Manage</button>
        </div>
      </div>
    </div>

    <div v-if="showManage" class="max-w-3xl mx-auto mt-3 bg-white p-4 rounded-lg border border-gray-200 shadow">
      <div class="flex items-center justify-between">
        <div>
          <h4 class="text-sm font-medium">Privacy preferences</h4>
          <p class="text-sm text-gray-600">Toggle analytics cookies for this browser/device.</p>
        </div>

        <label class="flex items-center cursor-pointer">
          <input type="checkbox" class="sr-only" v-model="analyticsEnabled" @change="onToggleAnalytics" />
          <div :class="analyticsEnabled ? 'bg-green-600' : 'bg-gray-200'"
            class="w-12 h-6 rounded-full relative transition-colors">
            <div :class="analyticsEnabled ? 'translate-x-6' : 'translate-x-0'"
              class="w-6 h-6 bg-white rounded-full shadow transform transition-transform absolute top-0 left-0"></div>
          </div>
        </label>
      </div>

      <div class="mt-3 text-right">
        <button @click="closeManage" class="px-3 py-1 text-sm bg-white border rounded">Close</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useToast } from '@/composables/useToast'
import { onMounted, ref } from 'vue'
import { disableAnalytics, enableAnalytics, isAnalyticsEnabled, setGlobalAnalyticsSetting } from '../services/analytics'

const visible = ref(false)
const showManage = ref(false)
const analyticsEnabled = ref(false)

const { toast } = useToast()

function setCookie(name, value, days = 365) {
  if (typeof document === 'undefined') return
  const expires = new Date(Date.now() + days * 864e5).toUTCString()
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`
}

function getCookie(name) {
  if (typeof document === 'undefined') return null
  const cookies = document.cookie ? document.cookie.split('; ') : []
  for (let i = 0; i < cookies.length; i++) {
    const parts = cookies[i].split('=')
    const key = parts.shift()
    const val = parts.join('=')
    if (key === name) return decodeURIComponent(val)
  }
  return null
}

function showBannerIfRequired() {
  try {
    const consent = getCookie('sanaapesa_cookie_consent')
    if (consent === 'accepted') {
      analyticsEnabled.value = true
      try { enableAnalytics() } catch (err) { console.warn('enableAnalytics failed', err) }
      visible.value = false
    } else if (consent === 'declined') {
      analyticsEnabled.value = false
      try { disableAnalytics() } catch (err) { console.warn('disableAnalytics failed', err) }
      visible.value = false
    } else {
      analyticsEnabled.value = !!isAnalyticsEnabled()
      visible.value = true
    }
  } catch (err) {
    visible.value = true
    console.warn('cookie consent init failed', err)
  }
}

function accept() {
  setCookie('sanaapesa_cookie_consent', 'accepted')
  analyticsEnabled.value = true
  try { enableAnalytics() } catch (err) { console.warn('enableAnalytics failed', err) }
  visible.value = false

  toast.success('Analytics accepted — thank you!', 5000, {
    label: 'Undo',
    callback() {
      setCookie('sanaapesa_cookie_consent', 'declined')
      analyticsEnabled.value = false
      try { disableAnalytics() } catch (e) { console.warn('disableAnalytics failed', e) }
    },
  })
}

function decline() {
  setCookie('sanaapesa_cookie_consent', 'declined')
  analyticsEnabled.value = false
  try { disableAnalytics() } catch (err) { console.warn('disableAnalytics failed', err) }
  visible.value = false

  toast.info('Analytics declined — your choice is saved.', 5000, {
    label: 'Undo',
    callback() {
      setCookie('sanaapesa_cookie_consent', 'accepted')
      analyticsEnabled.value = true
      try { enableAnalytics() } catch (e) { console.warn('enableAnalytics failed', e) }
    },
  })
}

function manage() {
  showManage.value = true
}

function closeManage() {
  showManage.value = false
}

async function onToggleAnalytics() {
  const previous = !analyticsEnabled.value
  try {
    if (analyticsEnabled.value) enableAnalytics()
    else disableAnalytics()
  } catch (err) {
    console.warn('toggling analytics failed', err)
  }

  setCookie('sanaapesa_cookie_consent', analyticsEnabled.value ? 'accepted' : 'declined')

  try {
    await setGlobalAnalyticsSetting(analyticsEnabled.value)
  } catch (err) {
    console.warn('setGlobalAnalyticsSetting failed', err)
  }

  toast.success(analyticsEnabled.value ? 'Analytics enabled' : 'Analytics disabled', 5000, {
    label: 'Undo',
    callback() {
      analyticsEnabled.value = previous
      try {
        if (previous) enableAnalytics()
        else disableAnalytics()
      } catch (err) {
        console.warn('revert analytics failed', err)
      }
      setCookie('sanaapesa_cookie_consent', previous ? 'accepted' : 'declined')
    },
  })
}

onMounted(showBannerIfRequired)
</script>

<style scoped>
/* small tweaks: keep styles in tailwind; extra CSS could go here if needed */
</style>
